import React from 'react';
import { useTheme } from '@mui/material';

interface SmallIntelliScoreWidgetProps {
  score: number;
  width?: number | string;
  height?: number | string;
  type?: 'onprem' | 'cloud' | 'cve';
}

const SmallIntelliScoreWidget: React.FC<SmallIntelliScoreWidgetProps> = ({ 
  score,
  width = 80,
  height = 80,
  type = 'onprem'
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const getGradientColors = (type: string): { start: string; end: string } => {
    // First determine color based on score - matching big widget thresholds
    if (score >= 70) {
      return { start: '#4CAF50', end: '#81C784' }; // Green gradient for high
    }
    if (score >= 40) {
      return { start: '#FF9800', end: '#FFB74D' }; // Orange gradient for medium
    }
    return { start: '#F44336', end: '#E57373' }; // Red gradient for low
  };

  const backgroundColor = isDarkMode ? "#2A2E39" : "#F1F1F1";
  const centerBgColor = isDarkMode ? "#353A47" : "#F3F5FF";
  const gradientColors = getGradientColors(type);
  
  const RADIUS = 27;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  // For score 100, we want to fill the complete circle
  const normalizedScore = Math.min(100, Math.max(0, score));
  const arcLength = (normalizedScore / 100) * CIRCUMFERENCE;
  const strokeDasharray = `${arcLength} ${CIRCUMFERENCE}`;

  // Debug logging for desktop screens
  console.log(`SmallIntelliScoreWidget Debug - Score: ${score}, Normalized: ${normalizedScore}, ArcLength: ${arcLength}, StrokeDasharray: ${strokeDasharray}, Type: ${type}`);

  // Create unique gradient ID to avoid conflicts
  const gradientId = `gradient-${type}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg width={width} height={height} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Definitions for gradients and filters - moved to top */}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={gradientColors.start} stopOpacity="1" />
          <stop offset="100%" stopColor={gradientColors.end} stopOpacity="1" />
        </linearGradient>
        <filter id="filter0_di_1816_41633" x="5" y="5" width="70" height="70" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="2" dy="2"/>
          <feGaussianBlur stdDeviation="3"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.565223 0 0 0 0 0.656755 0 0 0 0 1 0 0 0 0.15 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1816_41633"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1816_41633" result="shape"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="1" dy="1"/>
          <feGaussianBlur stdDeviation="1.5"/>
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.395798 0 0 0 0 0.400044 0 0 0 0 0.41703 0 0 0 0.1 0"/>
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_1816_41633"/>
        </filter>
      </defs>

      {/* Background circle */}
      <circle cx="40" cy="40" r="30" fill={backgroundColor}/>
      
      {/* Background track circle */}
      <circle 
        cx="40" 
        cy="40" 
        r={RADIUS}
        stroke={isDarkMode ? "rgba(58, 63, 75, 0.4)" : "rgba(225, 225, 225, 0.7)"} 
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />

      {/* Score arc - only show if score > 0 */}
      {score > 0 && (
        <>
          {/* Fallback solid color circle in case gradient fails */}
          <circle
            cx="40"
            cy="40"
            r={RADIUS}
            stroke={gradientColors.start}
            strokeWidth="6"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={strokeDasharray}
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dasharray 0.3s ease'
            }}
            strokeOpacity="0.8"
          />
          {/* Gradient circle */}
          <circle
            cx="40"
            cy="40"
            r={RADIUS}
            stroke={`url(#${gradientId})`}
            strokeWidth="6"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={strokeDasharray}
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dasharray 0.3s ease'
            }}
            strokeOpacity="1"
          />
        </>
      )}

      {/* Center circle */}
      <g filter="url(#filter0_di_1816_41633)">
        <circle cx="40" cy="40" r="24" fill={centerBgColor}/>
        <circle cx="40" cy="40" r="23.5" stroke={isDarkMode ? "#3A3F4B" : "white"}/>
      </g>

      {/* Score text */}
      <text
        x="40"
        y="40"
        textAnchor="middle"
        dominantBaseline="middle"
        fill={theme.palette.text.primary}
        fontSize="20"
        fontWeight="600"
      >
        {Math.round(score)}
      </text>
    </svg>
  );
};

export default SmallIntelliScoreWidget; 